import React, { useState, useEffect, useCallback } from 'react';
import { 
  Repeat,
  AlertTriangle,
  Check,
  X,
  Plus
} from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Schedule, RecurrencePattern, scheduleService, ScheduleConflict } from '../../services/scheduleService';
import { lessonService } from '../../services/lessonService';
import { classService } from '../../services/classService';
import { Lesson } from '../../types';

interface ScheduleFormProps {
  teacherId: string;
  schedule?: Schedule;
  onSave: (schedule: Schedule) => void;
  onCancel: () => void;
}

export const ScheduleForm: React.FC<ScheduleFormProps> = ({
  teacherId,
  schedule,
  onSave,
  onCancel
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [conflicts, setConflicts] = useState<ScheduleConflict[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [filteredLessons, setFilteredLessons] = useState<Lesson[]>([]);
  const [lessonSearchTerm, setLessonSearchTerm] = useState('');
  const [showLessonDropdown, setShowLessonDropdown] = useState(false);
  const [showClassModal, setShowClassModal] = useState(false);
  const [newClassData, setNewClassData] = useState({
    name: '',
    grade: '',
    description: ''
  });

  // Form state
  const [formData, setFormData] = useState({
    title: schedule?.title || '',
    description: schedule?.description || '',
    lessonId: schedule?.lessonId || '',
    className: schedule?.className || '',
    startDate: schedule?.startDate ? schedule.startDate.toISOString().split('T')[0] : '',
    endDate: schedule?.endDate ? schedule.endDate.toISOString().split('T')[0] : '',
    startTime: schedule?.startTime || '08:00',
    duration: schedule?.duration || 30,
    maxStudents: schedule?.maxStudents || 45,
    location: schedule?.location || '',
    notes: schedule?.notes || '',
    isActive: schedule?.isActive ?? true,
    recurrence: schedule?.recurrence || {
      type: 'none',
      interval: 1,
      daysOfWeek: [],
      dayOfMonth: 1,
      endAfterOccurrences: undefined,
      endDate: undefined
    } as RecurrencePattern
  });



  // Handle new class creation
  const handleCreateClass = async () => {
    if (!newClassData.name || !newClassData.grade) return;

    try {
      const newClass = await classService.createClass({
        name: newClassData.name,
        grade: newClassData.grade,
        description: newClassData.description,
        teacherId,
        isActive: true,
        academicYear: '2024-2025'
      });

      // Set the new class name in the form
      handleInputChange('className', newClass.name);
      
      // Reset form and close modal
      setNewClassData({ name: '', grade: '', description: '' });
      setShowClassModal(false);
    } catch (error) {
      console.error('Error creating class:', error);
    }
  };

  const loadLessons = useCallback(async () => {
    try {
      const fetchedLessons = await lessonService.getAllLessons(teacherId);
      setLessons(fetchedLessons);
      setFilteredLessons(fetchedLessons);
    } catch (error) {
      console.error('Error loading lessons:', error);
    }
  }, [teacherId]);

  // Filter lessons based on search term
  useEffect(() => {
    const filtered = lessons.filter(lesson => 
      lesson.metadata?.title?.toLowerCase().includes(lessonSearchTerm.toLowerCase()) ||
      lesson.metadata?.subject?.toLowerCase().includes(lessonSearchTerm.toLowerCase())
    );
    setFilteredLessons(filtered);
  }, [lessons, lessonSearchTerm]);

  // Set initial lesson search term if editing existing schedule
  useEffect(() => {
    if (schedule?.lessonId && lessons.length > 0) {
      const selectedLesson = lessons.find(lesson => lesson.metadata?.lesson_id === schedule.lessonId);
      if (selectedLesson) {
        setLessonSearchTerm(selectedLesson.metadata?.title || t('schedule.untitledLesson'));
      }
    }
  }, [schedule?.lessonId, lessons, t]);

  // Load lessons
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadLessons();
  }, [loadLessons]);

  // Handle form field changes
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle recurrence changes
  const handleRecurrenceChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      recurrence: {
        ...prev.recurrence,
        [field]: value
      }
    }));
  };

  // Handle day of week selection
  const handleDayToggle = (dayIndex: number) => {
    const currentDays = formData.recurrence.daysOfWeek || [];
    const newDays = currentDays.includes(dayIndex)
      ? currentDays.filter(d => d !== dayIndex)
      : [...currentDays, dayIndex].sort();
    
    handleRecurrenceChange('daysOfWeek', newDays);
  };

  // Check for conflicts
  const checkConflicts = useCallback(async () => {
    if (!formData.startDate || !formData.startTime) return;

    try {
      const scheduleData = {
        title: formData.title,
        lessonId: formData.lessonId,
        teacherId,
        className: formData.className,
        startDate: new Date(`${formData.startDate} ${formData.startTime}`),
        endDate: formData.endDate ? new Date(`${formData.endDate} ${formData.startTime}`) : undefined,
        startTime: formData.startTime,
        duration: formData.duration,
        recurrence: formData.recurrence,
        isActive: formData.isActive,
        maxStudents: formData.maxStudents,
        location: formData.location,
        notes: formData.notes
      };

      const foundConflicts = await scheduleService.checkConflicts(scheduleData, schedule?.id);
      setConflicts(foundConflicts);
    } catch (error) {
      console.error('Error checking conflicts:', error);
    }
  }, [formData, teacherId, schedule?.id]);

  // Check conflicts when relevant fields change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (formData.startDate && formData.startTime && formData.lessonId) {
      checkConflicts();
    }
  }, [formData, checkConflicts]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.lessonId || !formData.startDate || !formData.startTime) {
      return;
    }

    setIsLoading(true);
    try {
      const scheduleData = {
        title: formData.title,
        description: formData.description,
        lessonId: formData.lessonId,
        teacherId,
        className: formData.className || undefined,
        startDate: new Date(`${formData.startDate} ${formData.startTime}`),
        endDate: formData.endDate ? new Date(`${formData.endDate} ${formData.startTime}`) : undefined,
        startTime: formData.startTime,
        duration: formData.duration,
        recurrence: formData.recurrence,
        isActive: formData.isActive,
        maxStudents: formData.maxStudents,
        location: formData.location,
        notes: formData.notes
      };

      if (schedule?.id) {
        // Update existing schedule
        await scheduleService.updateSchedule(schedule.id, scheduleData);
        onSave({ ...schedule, ...scheduleData });
      } else {
        // Create new schedule
        const newSchedule = await scheduleService.createSchedule(scheduleData);
        onSave(newSchedule);
      }
    } catch (error) {
      console.error('Error saving schedule:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const dayNames = [t('schedule.sunday'), t('schedule.monday'), t('schedule.tuesday'), t('schedule.wednesday'), t('schedule.thursday'), t('schedule.friday'), t('schedule.saturday')];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {schedule ? t('schedule.editSchedule') : t('schedule.createSchedule')}
        </h2>
        <button
          onClick={onCancel}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('schedule.title')} *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('schedule.enterTitle')}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('schedule.lesson')} *
            </label>
            <div className="relative">
              <input
                type="text"
                value={lessonSearchTerm}
                onChange={(e) => {
                  setLessonSearchTerm(e.target.value);
                  setShowLessonDropdown(true);
                  // Clear selected lesson when searching
                  if (!e.target.value) {
                    handleInputChange('lessonId', '');
                  }
                }}
                onFocus={() => {
                  setShowLessonDropdown(true);
                  // Show all lessons when focusing on search if no search term
                  if (!lessonSearchTerm) {
                    setFilteredLessons(lessons);
                  }
                }}
                onBlur={() => {
                  // Delay hiding dropdown to allow for clicks
                  setTimeout(() => setShowLessonDropdown(false), 200);
                }}
                placeholder={t('schedule.searchLessons')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              {filteredLessons.length > 0 && showLessonDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredLessons.map((lesson) => (
                    <button
                      key={lesson.metadata?.lesson_id}
                      type="button"
                      onClick={() => {
                        handleInputChange('lessonId', lesson.metadata?.lesson_id);
                        setLessonSearchTerm(lesson.metadata?.title || t('schedule.untitledLesson'));
                        setShowLessonDropdown(false);
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                    >
                                      <div className="font-medium">{lesson.metadata?.title || t('schedule.untitledLesson')}</div>
                <div className="text-sm text-gray-500">
                  {lesson.metadata?.subject} - {t('lesson.grade')} {lesson.metadata?.grade}
                  {lesson.className && ` • ${lesson.className}`}
                </div>
                    </button>
                  ))}
                </div>
              )}
              {filteredLessons.length === 0 && lessonSearchTerm && showLessonDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                  <div className="px-3 py-2 text-sm text-gray-500">
                    {t('schedule.noLessonsFound')}
                  </div>
                </div>
              )}
              {/* Hidden input to store the actual lesson ID for form submission */}
              <input
                type="hidden"
                value={formData.lessonId}
                name="lessonId"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('schedule.class')} ({t('schedule.optional')})
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={formData.className}
              onChange={(e) => handleInputChange('className', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('schedule.enterClassName')}
            />
            <button
              type="button"
              onClick={() => setShowClassModal(true)}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1"
              title={t('schedule.createNewClass')}
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">{t('schedule.createNewClass')}</span>
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('schedule.description')}
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={t('schedule.enterDescription')}
          />
        </div>

        {/* Date and Time */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('schedule.startDate')} *
            </label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('schedule.startTime')} *
            </label>
            <input
              type="time"
              value={formData.startTime}
              onChange={(e) => handleInputChange('startTime', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('schedule.duration')} (minutes) *
            </label>
            <input
              type="number"
              value={formData.duration}
              onChange={(e) => handleInputChange('duration', parseInt(e.target.value))}
              min="15"
              max="480"
              step="15"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
        </div>

        {/* End Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('schedule.endDate')} ({t('schedule.optional')})
          </label>
          <input
            type="date"
            value={formData.endDate}
            onChange={(e) => handleInputChange('endDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Recurrence */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Repeat className="w-5 h-5 mr-2" />
            {t('schedule.recurrenceTitle')}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('schedule.recurrenceType')}
              </label>
              <select
                value={formData.recurrence.type}
                onChange={(e) => handleRecurrenceChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="none">{t('schedule.noRecurrence')}</option>
                <option value="daily">{t('schedule.daily')}</option>
                <option value="weekly">{t('schedule.weekly')}</option>
                <option value="monthly">{t('schedule.monthly')}</option>
              </select>
            </div>

            {formData.recurrence.type !== 'none' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('schedule.interval')}
                </label>
                <input
                  type="number"
                  value={formData.recurrence.interval}
                  onChange={(e) => handleRecurrenceChange('interval', parseInt(e.target.value))}
                  min="1"
                  max="52"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
          </div>

          {/* Weekly recurrence options */}
          {formData.recurrence.type === 'weekly' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('schedule.daysOfWeek')}
              </label>
              <div className="flex flex-wrap gap-2">
                {dayNames.map((day, index) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleDayToggle(index)}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      formData.recurrence.daysOfWeek?.includes(index)
                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                        : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Monthly recurrence options */}
          {formData.recurrence.type === 'monthly' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('schedule.dayOfMonth')}
              </label>
              <input
                type="number"
                value={formData.recurrence.dayOfMonth || 1}
                onChange={(e) => handleRecurrenceChange('dayOfMonth', parseInt(e.target.value))}
                min="1"
                max="31"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}

          {/* Recurrence end options */}
          {formData.recurrence.type !== 'none' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('schedule.endAfterOccurrences')} ({t('schedule.optional')})
              </label>
              <input
                type="number"
                value={formData.recurrence.endAfterOccurrences || ''}
                onChange={(e) => handleRecurrenceChange('endAfterOccurrences', e.target.value ? parseInt(e.target.value) : undefined)}
                min="1"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('schedule.enterOccurrences')}
              />
              <p className="text-sm text-gray-500 mt-1">
                {t('schedule.endAfterOccurrencesHelp')}
              </p>
            </div>
          )}
        </div>

        {/* Additional Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('schedule.maxStudents')}
            </label>
            <input
              type="number"
              value={formData.maxStudents || ''}
              onChange={(e) => handleInputChange('maxStudents', e.target.value ? parseInt(e.target.value) : undefined)}
              min="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('schedule.location')}
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('schedule.enterLocation')}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('schedule.notes')}
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={t('schedule.enterNotes')}
          />
        </div>

        {/* Conflicts Warning */}
        {conflicts.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800">
                  {t('schedule.conflictsDetected')}
                </h4>
                <ul className="mt-2 text-sm text-yellow-700 space-y-1">
                  {conflicts.map((conflict, index) => (
                    <li key={index}>• {t('schedule.conflictMessage', { date: conflict.date })}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {t('schedule.cancel')}
          </button>
          <button
            type="submit"
            disabled={isLoading || conflicts.length > 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{t('schedule.saving')}</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                <span>{schedule ? t('schedule.update') : t('schedule.create')}</span>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Create New Class Modal */}
      {showClassModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('schedule.createNewClass')}
              </h3>
              <button
                onClick={() => setShowClassModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('schedule.className')} *
                </label>
                <input
                  type="text"
                  value={newClassData.name}
                  onChange={(e) => setNewClassData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('schedule.enterClassName')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('schedule.grade')} *
                </label>
                <select
                  value={newClassData.grade}
                  onChange={(e) => setNewClassData(prev => ({ ...prev, grade: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('schedule.selectGrade')}</option>
                  <option value="1">{t('schedule.grade1')}</option>
                  <option value="2">{t('schedule.grade2')}</option>
                  <option value="3">{t('schedule.grade3')}</option>
                  <option value="4">{t('schedule.grade4')}</option>
                  <option value="5">{t('schedule.grade5')}</option>
                  <option value="6">{t('schedule.grade6')}</option>
                  <option value="7">{t('schedule.grade7')}</option>
                  <option value="8">{t('schedule.grade8')}</option>
                  <option value="9">{t('schedule.grade9')}</option>
                  <option value="10">{t('schedule.grade10')}</option>
                  <option value="11">{t('schedule.grade11')}</option>
                  <option value="12">{t('schedule.grade12')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('schedule.description')}
                </label>
                <textarea
                  value={newClassData.description}
                  onChange={(e) => setNewClassData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('schedule.enterClassDescription')}
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowClassModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('schedule.cancel')}
              </button>
              <button
                type="button"
                onClick={handleCreateClass}
                disabled={!newClassData.name || !newClassData.grade}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>{t('schedule.create')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
